import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { ContactDetails, ContactOpportunity } from "@/hooks/useContactDetails";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, X } from "lucide-react";
import { useAssistants } from "@/hooks/useAssistants";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useTeam } from "@/hooks/useTeam";
import { useTags } from "@/hooks/useTags";
import { usePipelines } from "@/hooks/usePipelines";
import { useContactManagement } from "@/hooks/useContactManagement";
import { TagSelectorModal } from "@/components/chat/TagSelectorModal";
import { EditOpportunityFullDialog, type OpportunityEditData } from "@/components/crm/EditOpportunityFullDialog";
import { WonOpportunityDialog } from "@/components/crm/WonOpportunityDialog";
import { LossReasonDialog } from "@/components/crm/LossReasonDialog";
import { OpportunityCard } from "./OpportunityCard";
import { Textarea } from "@/components/ui/textarea";
import type { WonOpportunityData } from "@/hooks/useOpportunities";

interface ContactProps {
  contact?: ContactDetails;
  onUpdate?: (updates: Partial<ContactDetails>) => Promise<boolean>;
  onRefresh?: () => Promise<void>;
}

const Contact: React.FC<ContactProps> = ({ contact, onUpdate, onRefresh }) => {
  const { user } = useAuth();
  const { assistants } = useAssistants();
  const { members } = useTeam();
  const { tags: availableTags, fetchTags } = useTags();
  const { pipelines, pipelineStages } = usePipelines();
  const contactManagement = useContactManagement();

  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isCampaignModalOpen, setIsCampaignModalOpen] = useState(false);
  const [isOppEditModalOpen, setIsOppEditModalOpen] = useState(false);
  const [isCreateOppModalOpen, setIsCreateOppModalOpen] = useState(false);
  const [newCampaign, setNewCampaign] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [attributionSearch, setAttributionSearch] = useState("");
  const [contactTags, setContactTags] = useState<string[]>([]);
  const [newOppForm, setNewOppForm] = useState({
    title: '',
    pipeline_id: '',
    stage_id: '',
    product: '',
    price: '',
  });

  const [selectedOpportunity, setSelectedOpportunity] = useState<ContactOpportunity | null>(null);
  const [wonDialogOpen, setWonDialogOpen] = useState(false);
  const [lossDialogOpen, setLossDialogOpen] = useState(false);
  const [pendingEditData, setPendingEditData] = useState<OpportunityEditData | null>(null);

  useEffect(() => {
    setContactTags(contact?.tags || []);
  }, [contact?.tags]);

  if (!contact) return null;

  const handleApplyTags = async (newTags: string[]) => {
    const success = await contactManagement.setTags(contact.id, newTags);
    if (success) {
      setContactTags(newTags);
      onRefresh?.();
    }
  };

  const handleRemoveTag = async (tagToRemove: string) => {
    const success = await contactManagement.removeTag(contact.id, tagToRemove);
    if (success) {
      setContactTags(prev => prev.filter(t => t !== tagToRemove));
      onRefresh?.();
    }
  };

  const handleTagCreated = () => {
    fetchTags();
  };

  const handleAddCampaign = async () => {
    if (!newCampaign.trim() || !onUpdate) return;

    setIsSaving(true);
    try {
      const updatedCampaigns = [...(contact.campaigns || []), newCampaign.trim()];
      const success = await onUpdate({ campaigns: updatedCampaigns });
      if (success) {
        setNewCampaign("");
        setIsCampaignModalOpen(false);
        onRefresh?.();
      }
    } catch (error) {
      console.error("Error adding campaign:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveCampaign = async (campaignToRemove: string) => {
    if (!onUpdate) return;

    const updatedCampaigns = (contact.campaigns || []).filter(c => c !== campaignToRemove);
    await onUpdate({ campaigns: updatedCampaigns });
    onRefresh?.();
  };

  const handleCreateOpportunity = async () => {
    if (!user?.account_id || !supabase || !contact) return;
    if (!newOppForm.title.trim() || !newOppForm.pipeline_id || !newOppForm.stage_id) return;

    setIsSaving(true);
    try {
      const priceValue = newOppForm.price ? parseFloat(newOppForm.price.replace(/\D/g, '')) / 100 : 0;
      const selectedStage = pipelineStages.find(s => s.id === newOppForm.stage_id);
      const stageType = selectedStage?.stage_type || 'normal';
      const derivedStatus = stageType === 'won' ? 'won' : stageType === 'lost' ? 'lost' : 'open';
      const { error } = await supabase
        .from('account_opportunities')
        .insert({
          account_id: user.account_id,
          contact_id: contact.id,
          pipeline_id: newOppForm.pipeline_id,
          stage_id: newOppForm.stage_id,
          title: newOppForm.title.trim(),
          product: newOppForm.product.trim() || null,
          price: priceValue,
          status: derivedStatus,
          tags: [],
          order_index: 0,
        });

      if (error) throw error;

      setIsCreateOppModalOpen(false);
      setNewOppForm({ title: '', pipeline_id: '', stage_id: '', product: '', price: '' });
      onRefresh?.();
    } catch (error) {
      console.error("Error creating opportunity:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditOpportunity = (opp: ContactOpportunity) => {
    setSelectedOpportunity(opp);
    setIsOppEditModalOpen(true);
  };

  const handleSaveOpportunityFull = async (data: OpportunityEditData) => {
    if (!user?.account_id || !selectedOpportunity || !supabase) return;

    const previousStatus = selectedOpportunity.status;

    if (data.status === 'won' && previousStatus !== 'won') {
      setPendingEditData(data);
      setIsOppEditModalOpen(false);
      setWonDialogOpen(true);
      return;
    }

    if (data.status === 'lost' && previousStatus !== 'lost') {
      setPendingEditData(data);
      setIsOppEditModalOpen(false);
      setLossDialogOpen(true);
      return;
    }

    await saveOpportunityUpdate(data);
  };

  const saveOpportunityUpdate = async (data: OpportunityEditData, extraData?: Record<string, unknown>) => {
    if (!user?.account_id || !selectedOpportunity || !supabase) return;

    const updateData: Record<string, unknown> = {
      title: data.title,
      pipeline_id: data.pipeline_id,
      stage_id: data.stage_id,
      product: data.product || null,
      price: data.price || 0,
      status: data.status,
      notes: data.notes || null,
      ...extraData,
    };

    if (data.status === 'won' || data.status === 'lost') {
      updateData.closed_at = data.closed_at || new Date().toISOString();
    } else {
      updateData.closed_at = null;
      updateData.sale_date = null;
      updateData.won_by_user_id = null;
      updateData.loss_reason_id = null;
    }

    const { error } = await supabase
      .from('account_opportunities')
      .update(updateData)
      .eq('id', selectedOpportunity.id)
      .eq('account_id', user.account_id);

    if (error) throw error;

    setIsOppEditModalOpen(false);
    setSelectedOpportunity(null);
    setPendingEditData(null);
    onRefresh?.();
  };

  const handleWonConfirm = async (wonData: WonOpportunityData) => {
    if (!pendingEditData) return;
    await saveOpportunityUpdate(pendingEditData, {
      product: wonData.product || pendingEditData.product || null,
      price: wonData.price || pendingEditData.price || 0,
      sale_date: wonData.sale_date || null,
      won_by_user_id: wonData.won_by_user_id || null,
      notes: wonData.notes || pendingEditData.notes || null,
      loss_reason_id: null,
    });
    setWonDialogOpen(false);
  };

  const handleLossConfirm = async (lossReasonId: string) => {
    if (!pendingEditData) return;
    await saveOpportunityUpdate(pendingEditData, {
      loss_reason_id: lossReasonId,
      sale_date: null,
      won_by_user_id: null,
    });
    setLossDialogOpen(false);
  };

  const handleAssistantToggle = async (checked: boolean) => {
    if (!onUpdate) return;
    await onUpdate({ assistant_is_active: checked });
    onRefresh?.();
  };

  const handleAssistantChange = async (assistantId: string) => {
    if (!onUpdate) return;
    await onUpdate({ assistant_id: assistantId === 'none' ? null : assistantId });
    onRefresh?.();
  };

  const handleAttributionChange = async (attributionId: string) => {
    if (!onUpdate) return;
    await onUpdate({ attribution_id: attributionId === 'none' ? null : attributionId });
    onRefresh?.();
  };

  const sortedOpportunities = [...(contact.opportunities || [])].sort((a, b) => {
    const statusOrder = { open: 0, won: 1, lost: 2 };
    const statusDiff = statusOrder[a.status] - statusOrder[b.status];
    if (statusDiff !== 0) return statusDiff;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  return (
    <div className="w-full flex flex-col gap-3 xl:order-3 order-2">
      {/* Assistant Section */}
      <div className="w-full p-4 gap-3 flex rounded-[12px] flex-col bg-[#18181B] border-[1px] border-[#3F3F464D]">
        <div className="w-full flex justify-between items-center gap-2">
          <h3 className="text-base font-semibold text-neutral-50">
            Assistente Ligado
          </h3>
          <Switch
            checked={contact.assistant_is_active}
            onCheckedChange={handleAssistantToggle}
            activeColor="data-[state=checked]:bg-[#22c55e]"
            inactiveColor="data-[state=unchecked]:bg-[#3F3F46]"
            thumbColor="bg-white"
          />
        </div>
        <Select
          value={contact.assistant_id || 'none'}
          onValueChange={handleAssistantChange}
        >
          <SelectTrigger className="w-full rounded-[12px] bg-[#18181B]">
            <SelectValue placeholder="Selecione um assistente" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="none">Nenhum assistente</SelectItem>
              {assistants.filter(a => a.is_active).map((assistant) => (
                <SelectItem key={assistant.id} value={assistant.id}>
                  {assistant.name}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      {/* Attribution Section */}
      <div className="w-full p-4 gap-3 flex rounded-[12px] flex-col bg-[#18181B] border-[1px] border-[#3F3F464D]">
        <h3 className="text-base font-semibold text-neutral-50">Responsável pelo Contato</h3>
        <Select
          value={contact.attribution_id || 'none'}
          onValueChange={handleAttributionChange}
          onOpenChange={(open) => {
            if (!open) setAttributionSearch('');
          }}
        >
          <SelectTrigger className="w-full rounded-[12px] bg-[#18181B]">
            <SelectValue placeholder="Selecione um responsável" />
          </SelectTrigger>
          <SelectContent>
            <div className="px-2 pb-2">
              <Input
                placeholder="Pesquisar responsável..."
                value={attributionSearch}
                onChange={(e) => setAttributionSearch(e.target.value)}
                className="h-8 bg-neutral-700 border-neutral-600 text-neutral-50"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            <SelectGroup>
              <SelectItem value="none">Sem responsável</SelectItem>
              {members
                .filter(m => m.is_active && m.auth_user_id)
                .filter(m => {
                  if (!attributionSearch) return true;
                  const searchLower = attributionSearch.toLowerCase();
                  const name = (m.name || '').toLowerCase();
                  const email = (m.email || '').toLowerCase();
                  return name.includes(searchLower) || email.includes(searchLower);
                })
                .map((member) => (
                  <SelectItem key={member.auth_user_id} value={member.auth_user_id!}>
                    {member.name || member.email}
                  </SelectItem>
                ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      {/* Opportunities Section */}
      <div className="w-full p-4 gap-3 flex rounded-[12px] flex-col bg-[#18181B] border-[1px] border-[#3F3F464D]">
        <div className="w-full flex justify-between items-center">
          <h3 className="text-base font-semibold text-neutral-50">Oportunidades</h3>
          <button onClick={() => setIsCreateOppModalOpen(true)}>
            <img src="/add_circle.svg" className="w-[24px] h-[24px]" alt="Adicionar oportunidade" />
          </button>
        </div>
        <div className="flex flex-col gap-3 w-full">
          {sortedOpportunities.length > 0 ? (
            sortedOpportunities.map((opp) => (
              <OpportunityCard
                key={opp.id}
                opportunity={opp}
                onEdit={handleEditOpportunity}
              />
            ))
          ) : (
            <p className="text-sm text-neutral-500">Nenhuma oportunidade</p>
          )}
        </div>
      </div>

      {/* Tags Section */}
      <div className="w-full p-4 gap-3 flex rounded-[12px] flex-col bg-[#18181B] border-[1px] border-[#3F3F464D]">
        <h3 className="text-base font-semibold text-neutral-50">Tags</h3>
        <div className="flex flex-wrap gap-2 w-full">
          <Badge
            variant="default2"
            className="bg-neutral-800 hover:bg-neutral-700 flex items-center cursor-pointer px-2 py-1 transition-colors"
            onClick={() => setIsTagModalOpen(true)}
          >
            <Plus className="w-4 h-4 mr-1" />
            <span>Adicionar</span>
          </Badge>

          {contactTags.map((tag) => (
            <Badge
              key={tag}
              className="capitalize flex items-center gap-1 pr-1 group cursor-pointer hover:bg-neutral-700 transition-colors"
            >
              <span>{tag}</span>
              <button
                onClick={() => handleRemoveTag(tag)}
                className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
                disabled={contactManagement.updating}
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}

          {contactTags.length === 0 && (
            <p className="text-xs text-neutral-500">Nenhuma tag atribuída</p>
          )}
        </div>
      </div>

      {/* Campaigns Section */}
      <div className="w-full p-4 gap-3 flex rounded-[12px] flex-col bg-[#18181B] border-[1px] border-[#3F3F464D]">
        <div className="w-full flex justify-between items-center">
          <h3 className="text-base font-semibold text-neutral-50">Campanhas</h3>
          <button onClick={() => setIsCampaignModalOpen(true)}>
            <img src="/add_circle.svg" className="w-[24px] h-[24px]" alt="Adicionar campanha" />
          </button>
        </div>
        <div className="flex flex-wrap gap-2 w-full">
          {contact.campaigns && contact.campaigns.length > 0 ? (
            contact.campaigns.map((campaign, index) => (
              <Badge key={index} className="cursor-pointer py-1 px-3 hover:bg-destructive/80 transition-colors" onClick={() => handleRemoveCampaign(campaign)}>
                {campaign}
              </Badge>
            ))
          ) : (
            <p className="text-sm text-neutral-500">Nenhuma campanha</p>
          )}
        </div>
      </div>

      <TagSelectorModal
        open={isTagModalOpen}
        onOpenChange={setIsTagModalOpen}
        availableTags={availableTags}
        selectedTags={contactTags}
        onApplyTags={handleApplyTags}
        onTagCreated={handleTagCreated}
      />

      {/* Campaign Modal */}
      <Dialog open={isCampaignModalOpen} onOpenChange={setIsCampaignModalOpen}>
        <DialogContent className="bg-[#18181B] border-[#3F3F464D]">
          <DialogHeader>
            <DialogTitle className="text-white">Adicionar Campanha</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="campaign-name" className="text-white">Nome da Campanha</Label>
              <Input
                id="campaign-name"
                value={newCampaign}
                onChange={(e) => setNewCampaign(e.target.value)}
                placeholder="Digite o nome da campanha"
                className="bg-neutral-600 text-white border-[#3F3F46]"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setIsCampaignModalOpen(false)}
                className="bg-transparent border-[#3F3F46]"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleAddCampaign}
                disabled={!newCampaign.trim() || isSaving}
                className="bg-[#E4E4E7]"
              >
                {isSaving ? "Salvando..." : "Adicionar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Opportunity Modal */}
      <Dialog open={isCreateOppModalOpen} onOpenChange={setIsCreateOppModalOpen}>
        <DialogContent className="bg-[#18181B] border-[#3F3F464D] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">Nova Oportunidade</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label className="text-white">Título *</Label>
              <Input
                value={newOppForm.title}
                onChange={(e) => setNewOppForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Título da oportunidade"
                className="bg-neutral-600 text-white border-[#3F3F46] placeholder:text-gray-400"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-2">
                <Label className="text-white">Pipeline *</Label>
                <Select
                  value={newOppForm.pipeline_id}
                  onValueChange={(value) => {
                    setNewOppForm(prev => ({ ...prev, pipeline_id: value, stage_id: '' }));
                  }}
                >
                  <SelectTrigger className="w-full bg-neutral-600 text-white border-[#3F3F46]">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {pipelines.map((pipeline) => (
                        <SelectItem key={pipeline.id} value={pipeline.id}>
                          {pipeline.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2">
                <Label className="text-white">Etapa *</Label>
                <Select
                  value={newOppForm.stage_id}
                  onValueChange={(value) => setNewOppForm(prev => ({ ...prev, stage_id: value }))}
                  disabled={!newOppForm.pipeline_id}
                >
                  <SelectTrigger className="w-full bg-neutral-600 text-white border-[#3F3F46]">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {pipelineStages
                        .filter(s => s.pipeline_id === newOppForm.pipeline_id)
                        .map((stage) => (
                          <SelectItem key={stage.id} value={stage.id}>
                            {stage.name}
                          </SelectItem>
                        ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-white">Produto/Serviço</Label>
              <Input
                value={newOppForm.product}
                onChange={(e) => setNewOppForm(prev => ({ ...prev, product: e.target.value }))}
                placeholder="Nome do produto ou serviço"
                className="bg-neutral-600 text-white border-[#3F3F46] placeholder:text-gray-400"
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label className="text-white">Valor (R$)</Label>
              <Input
                value={newOppForm.price}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/\D/g, '');
                  if (!rawValue) { setNewOppForm(prev => ({ ...prev, price: '' })); return; }
                  const num = parseInt(rawValue, 10) / 100;
                  setNewOppForm(prev => ({ ...prev, price: num.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }));
                }}
                placeholder="0,00"
                className="bg-neutral-600 text-white border-[#3F3F46] placeholder:text-gray-400"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setIsCreateOppModalOpen(false)}
                className="bg-transparent border-[#3F3F46] text-white"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreateOpportunity}
                disabled={!newOppForm.title.trim() || !newOppForm.pipeline_id || !newOppForm.stage_id || isSaving}
                className="bg-[#E4E4E7]"
              >
                {isSaving ? "Criando..." : "Criar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Opportunity Edit Modal */}
      <EditOpportunityFullDialog
        open={isOppEditModalOpen}
        onClose={() => {
          setIsOppEditModalOpen(false);
          setSelectedOpportunity(null);
        }}
        onSave={handleSaveOpportunityFull}
        opportunity={selectedOpportunity}
      />

      {/* Won Opportunity Dialog */}
      <WonOpportunityDialog
        open={wonDialogOpen}
        onClose={() => {
          setWonDialogOpen(false);
          setPendingEditData(null);
        }}
        onConfirm={handleWonConfirm}
        opportunityTitle={selectedOpportunity?.title}
        initialProduct={pendingEditData?.product}
        initialPrice={pendingEditData?.price}
      />

      {/* Loss Reason Dialog */}
      <LossReasonDialog
        open={lossDialogOpen}
        onClose={() => {
          setLossDialogOpen(false);
          setPendingEditData(null);
        }}
        onConfirm={handleLossConfirm}
        opportunityTitle={selectedOpportunity?.title}
      />
    </div>
  );
};

export default Contact;
